import { FloraButton } from '@grupoboticario/flora-react';
import { Carousel, CarouselContent } from '@grupoboticario/flora-team-react-carousel';
import slugify from 'slugify';

import { CardContent, CardContentSkeleton, ResponsiveFloraTypography } from 'components';
import { SLIDES_TO_SHOW } from 'config/environments/shared';
import { isMobile as isMobileHelper } from 'helpers';
import { useStarlinkContent, useTracking } from 'hooks';
import { useGlobalContext } from 'store';

import Styles from './HomeContent.styles';

import type { HomeContentProps, HomeContentStarlinkType, ListToShare } from 'domains/content';

const selectDataToHome = (items: HomeContentProps[] = []) => {
  const element = items.find((item) => item?.data?.showInHome);
  return element?.data;
};

export const HomeContent = () => {
  const { document } = useGlobalContext();
  const { sendDataToGTM } = useTracking();
  const isMobile = isMobileHelper();
  const {
    data: starlinkData,
    isLoading,
    isSuccess,
    isError,
  } = useStarlinkContent<HomeContentStarlinkType>({
    document: document || '',
  });
  const pageContent = selectDataToHome(starlinkData?.items);

  const convertToURl = (category: string, title: string) => {
    const subDomain = category.replaceAll('_', '-').toLowerCase();
    const domain = encodeURIComponent(title);

    return `/${subDomain}/${domain}`;
  };

  const goToInternalPage = () => {
    window.location.replace(convertToURl(document || '', pageContent?.title || ''));
    sendDataToGTM({
      event: 'analytics-event',
      event_parameters: {
        event_name: 'interaction_novidades_ciclo',
        cd_interaction_detail: 'click:ver-tudo:top',
        cd_section: slugify(pageContent?.title, { lower: true }),
      },
    });
  };

  const handleCarouselInteraction = (event: React.MouseEvent<HTMLButtonElement> | number) => {
    const interactions: { [key: string]: string } = {
      prev: 'click:anterior',
      next: 'click:proximo',
    };
    const analyticsData = {
      event: 'analytics-event',
      event_parameters: {
        event_name: 'interaction_novidades_ciclo',
        cd_section: slugify(pageContent?.title, { lower: true }),
      },
    };

    if (typeof event === 'number') {
      const index = event as number;

      sendDataToGTM({
        ...analyticsData,
        event_parameters: {
          ...analyticsData.event_parameters,
          cd_interaction_detail: `click:carrossel:${index + 1}`,
        },
      });

      return;
    }

    sendDataToGTM({
      ...analyticsData,
      event_parameters: {
        ...analyticsData.event_parameters,
        cd_interaction_detail: interactions[event.currentTarget.dataset.interaction || ''],
      },
    });
  };

  if (isLoading) {
    return (
      <Styles.SkeletonWrapper>
        {Array.from({ length: 3 }).map((_, index) => (
          <CardContentSkeleton key={`skeleton-card-${index}`} />
        ))}
      </Styles.SkeletonWrapper>
    );
  }

  if (isSuccess && !pageContent) {
    return null;
  }

  if (isError) {
    return null;
  }

  return (
    <Styles.Wrapper direction='column'>
      <Styles.TitleWrapper gap='$4' align='center' justify='flex-start'>
        <ResponsiveFloraTypography fontSize='subtitle' fontWeight='medium' as='h6'>
          {pageContent?.title}
        </ResponsiveFloraTypography>
        <FloraButton size='small' onClick={goToInternalPage} hierarchy='secondary'>
          <ResponsiveFloraTypography fontSize='bodySmallShort' fontWeight='medium'>
            Ver tudo
          </ResponsiveFloraTypography>
        </FloraButton>
      </Styles.TitleWrapper>
      <Styles.Description fontSize='subtitle'>{pageContent?.description}</Styles.Description>
      {pageContent?.listToShare &&
      pageContent?.listToShare.length <= SLIDES_TO_SHOW[isMobile ? 'mobile' : 'desktop'] ? (
        <Styles.WithoutCarouselWrapper>
          {pageContent?.listToShare?.map((item: ListToShare, index: number) => (
            <CardContent
              {...item}
              key={index}
              analyticsData={{
                index: index + 1,
                section: pageContent.title,
              }}
            />
          ))}
        </Styles.WithoutCarouselWrapper>
      ) : (
        <Carousel variant='fluid' onDotPress={handleCarouselInteraction}>
          <Styles.PreviousButton data-interaction='prev' onClick={handleCarouselInteraction} />

          <CarouselContent
            infinite={false}
            slidesToShow={SLIDES_TO_SHOW[isMobile ? 'mobile' : 'desktop']}
          >
            {pageContent?.listToShare?.map((item: ListToShare, index: number) => (
              <Styles.CarouselWrapper key={index}>
                <CardContent
                  {...item}
                  analyticsData={{
                    index: index + 1,
                    section: pageContent.title,
                  }}
                />
              </Styles.CarouselWrapper>
            ))}
          </CarouselContent>
          <Styles.NextButton data-interaction='next' onClick={handleCarouselInteraction} />
        </Carousel>
      )}
    </Styles.Wrapper>
  );
};
